import React from 'react';
import { Box, Button, IconButton, Switch, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import { CustomDatePicker } from '../../../../components/DatePicker/CustomDatePicker';
import {
    clampDateToMin,
    datePickerValueToYyyyMmDd,
    maxDateYmd,
    sanitizePhaseRow,
    todayStart,
    todayYmd,
} from '../dateUtils';
import { ContestRichSelect } from '../ContestRichSelect';
import { getPhaseSelectOptions } from '../contestSelectOptions';
import { phaseOptionsForCampaign } from '../phaseConfig';
import type { ContestWizardState } from '../types';
import { contestWizardDatePickerSx, editorialHeadlineSx, editorialLabelSx, midnight } from '../midnightStyles';

type PhaseRow = ContestWizardState['contest_phase_dates'][number];

interface Props {
    state: ContestWizardState;
    onChange: (patch: Partial<ContestWizardState>) => void;
    onOpenAiAssist: () => void;
}

function normalizeStoredDate(val: string | undefined, minYmd: string): string | undefined {
    if (!val) return undefined;
    const d = dayjs(val).startOf('day');
    if (!d.isValid()) return undefined;
    const min = dayjs(minYmd).startOf('day');
    const effective = d.isBefore(min) ? min : d;
    return effective.format('YYYY-MM-DD');
}

function phaseOptionsForState(state: ContestWizardState): string[] {
    return phaseOptionsForCampaign(state.submission_state);
}

function defaultPhaseRow(state: ContestWizardState): PhaseRow {
    const options = phaseOptionsForState(state);
    return { phase: options[0] || 'submission', date_started: '', expiration_date: '' };
}

export function TimelinePhasesStep({ state, onChange, onOpenAiAssist }: Props) {
    const phaseOptions = phaseOptionsForState(state);
    const phases = state.contest_phase_dates;

    const setPhases = (next: PhaseRow[]) => onChange({ contest_phase_dates: next });

    const updatePhase = (index: number, patch: Partial<PhaseRow>) => {
        const today = todayYmd();
        const current = phases[index];
        const nextPatch = { ...patch };

        if (nextPatch.date_started !== undefined) {
            nextPatch.date_started = clampDateToMin(nextPatch.date_started, today);
            const minEnd = maxDateYmd(today, nextPatch.date_started);
            if (current.expiration_date && dayjs(current.expiration_date).isBefore(dayjs(minEnd), 'day')) {
                nextPatch.expiration_date = minEnd;
            }
        }

        if (nextPatch.expiration_date !== undefined) {
            const start = nextPatch.date_started ?? current.date_started;
            const minEnd = start ? maxDateYmd(today, start) : today;
            nextPatch.expiration_date = clampDateToMin(nextPatch.expiration_date, minEnd);
        }

        const next = phases.map((row, i) =>
            i === index ? sanitizePhaseRow({ ...row, ...nextPatch }) : sanitizePhaseRow(row),
        );
        setPhases(next);
    };

    const addPhase = () => {
        const used = new Set(phases.map(p => p.phase));
        const nextPhase = phaseOptions.find(p => !used.has(p));
        if (!nextPhase) return;
        setPhases([...phases, { phase: nextPhase, date_started: '', expiration_date: '' }]);
    };

    const removePhase = (index: number) => {
        if (phases.length <= 1) return;
        setPhases(phases.filter((_, i) => i !== index));
    };

    const togglePhases = (enabled: boolean) => {
        onChange({
            contest_phases_enabled: enabled,
            contest_phase_dates: enabled && phases.length === 0 ? [defaultPhaseRow(state)] : phases,
        });
    };

    const canAddPhase = phases.length < phaseOptions.length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                Campaign Timeline
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: midnight.surfaceLow,
                    p: 3,
                    borderRadius: '12px',
                }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, color: midnight.onSurface, mb: 0.5 }}>
                        Enable campaign phases
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant }}>
                        Schedule preparation, submission, judging, voting, and more.
                    </Typography>
                </Box>
                <Switch
                    checked={state.contest_phases_enabled}
                    onChange={e => togglePhases(e.target.checked)}
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: midnight.primary },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: 'rgba(255,201,101,0.25)',
                        },
                    }}
                />
            </Box>

            {state.contest_phases_enabled && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: '12px',
                            border: `1px solid ${midnight.outline}`,
                        }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <AutoAwesomeIcon sx={{ color: midnight.secondary }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>AI Assistant</Typography>
                                <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mb: 2 }}>
                                    Select every phase your campaign needs, which one opens first, start date, and
                                    duration — we will schedule them in order.
                                </Typography>
                                <Button
                                    onClick={onOpenAiAssist}
                                    sx={{
                                        color: midnight.secondary,
                                        fontWeight: 700,
                                        fontSize: 11,
                                        letterSpacing: '0.15em',
                                        textTransform: 'uppercase',
                                        p: 0,
                                        '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
                                    }}>
                                    Generate timeline →
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                    {phases.map((row, index) => {
                        const today = todayYmd();
                        const startYmd = normalizeStoredDate(row.date_started, today);
                        const minEndYmd = startYmd ? maxDateYmd(today, startYmd) : today;
                        const endYmd = normalizeStoredDate(row.expiration_date, minEndYmd);
                        const allowedPhases = phaseOptions.filter(
                            p => p === row.phase || !phases.some((r, i) => i !== index && r.phase === p),
                        );
                        const phaseSelectOptions = getPhaseSelectOptions(state.submission_state).filter(o =>
                            allowedPhases.includes(o.value),
                        );

                        return (
                            <Box
                                key={`${row.phase}-${index}`}
                                sx={{
                                    bgcolor: midnight.surfaceLow,
                                    p: 3,
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 2,
                                    }}>
                                    <Typography sx={editorialLabelSx}>Phase {index + 1}</Typography>
                                    {phases.length > 1 && (
                                        <IconButton
                                            size="small"
                                            aria-label="Remove phase"
                                            onClick={() => removePhase(index)}
                                            sx={{ color: midnight.onSurfaceVariant }}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    <ContestRichSelect
                                        label="Phase"
                                        value={row.phase}
                                        options={phaseSelectOptions}
                                        onChange={v => updatePhase(index, { phase: v })}
                                    />

                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                            gap: 2,
                                        }}>
                                        <Box>
                                            <Typography sx={{ ...editorialLabelSx, mb: 1 }}>Start date</Typography>
                                            <CustomDatePicker
                                                brandLuxe
                                                calendarOnly
                                                value={startYmd}
                                                onChange={val =>
                                                    updatePhase(index, {
                                                        date_started: datePickerValueToYyyyMmDd(val),
                                                    })
                                                }
                                                minDate={todayStart()}
                                                withTime={false}
                                                withTimezone={false}
                                                sx={contestWizardDatePickerSx}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ ...editorialLabelSx, mb: 1 }}>End date</Typography>
                                            <CustomDatePicker
                                                brandLuxe
                                                calendarOnly
                                                value={endYmd}
                                                onChange={val =>
                                                    updatePhase(index, {
                                                        expiration_date: datePickerValueToYyyyMmDd(val),
                                                    })
                                                }
                                                minDate={dayjs(minEndYmd).startOf('day')}
                                                withTime={false}
                                                withTimezone={false}
                                                sx={contestWizardDatePickerSx}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}

                    <Button
                        startIcon={<AddIcon />}
                        onClick={addPhase}
                        disabled={!canAddPhase}
                        sx={{
                            alignSelf: 'flex-start',
                            color: midnight.primary,
                            fontWeight: 700,
                            fontSize: 12,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            '&:disabled': { color: midnight.onSurfaceVariant, opacity: 0.5 },
                        }}>
                        Add phase
                    </Button>

                    {!canAddPhase && phases.length > 0 && (
                        <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant }}>
                            All available phase types are in use.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
